import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import TopTabbar from "../components/partials/TopTabbar";
import Header from "../components/partials/Header";
import Footer from "../components/partials/Footer";
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';
import Swal from 'sweetalert2';
import { AuthContext } from '../context/AuthContext';
import LoadingSpinner from '../utils/LoadingSpinner'; // Assurez-vous que le chemin est correct

const ResetPasswordPage = () => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [userId, setUserId] = useState(null);
    const [username, setUsername] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const { logout } = useContext(AuthContext);
    const navigate = useNavigate();

    // États pour les messages d'erreur en temps réel
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmNewPasswordError, setConfirmNewPasswordError] = useState('');

    // Vérifier si une redirection est nécessaire au chargement
    useEffect(() => {
        const startTime = Date.now(); // Enregistrer le temps de début

        const loadData = async () => {
            const shouldLogout = localStorage.getItem('shouldLogoutAfterPasswordChange');
            if (shouldLogout === 'true') {
                localStorage.removeItem('shouldLogoutAfterPasswordChange');
                logout();
                navigate('/login'); // Rediriger vers la page de login
            }

            const currentUser = AuthService.getCurrentUser();
            if (currentUser) {
                setUserId(currentUser.id);
                setUsername(currentUser.username);
            }

            // Calculer le temps écoulé
            const elapsedTime = Date.now() - startTime;
            // S'assurer que le chargement dure au moins 2 secondes
            const remainingTime = Math.max(0, 2000 - elapsedTime);

            if (remainingTime > 0) {
                await new Promise(resolve => setTimeout(resolve, remainingTime));
            }

            setIsPageLoading(false);
        };

        loadData();
    }, [logout, navigate]);

    const toggleOldPasswordVisibility = () => setShowOldPassword(!showOldPassword);
    const toggleNewPasswordVisibility = () => setShowNewPassword(!showNewPassword);
    const toggleConfirmNewPasswordVisibility = () => setShowConfirmNewPassword(!showConfirmNewPassword);

    const handleNewPasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        setNewPasswordError(value.length < 6 ? 'Le mot de passe doit contenir au moins 6 caractères.' : '');
    };

    const handleConfirmNewPasswordChange = (e) => {
        const value = e.target.value;
        setConfirmNewPassword(value);
        setConfirmNewPasswordError(value !== newPassword ? 'Les mots de passe ne correspondent pas.' : '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (oldPassword.trim() === '' || newPassword.length < 6 || newPassword !== confirmNewPassword) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Veuillez vérifier vos informations.'
            });
            return;
        }

        setLoading(true);
        try {
            const requestBody = { userId, username, oldPassword, newPassword };
            await UserService.initPassword(requestBody);

            // Marquer qu'une déconnexion est nécessaire
            localStorage.setItem('shouldLogoutAfterPasswordChange', 'true');

            Swal.fire({
                title: 'Succès',
                html: 'Votre mot de passe a été modifié avec succès.<br/>Vous serez déconnecté automatiquement.',
                icon: 'success',
                allowOutsideClick: false,
                allowEscapeKey: false,
                confirmButtonText: 'OK'
            }).then(() => {
                logout();
                navigate('/login');
            });

            // Réinitialiser le formulaire
            setOldPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setNewPasswordError('');
            setConfirmNewPasswordError('');
        } catch (error) {
            console.error('Erreur:', error.message || error);
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: error.response?.data?.message || 'Une erreur est survenue lors de la modification du mot de passe.'
            });
        } finally {
            setLoading(false);
        }
    };

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement..." />;
    }

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <div className="flex-grow-1 overflow-y-scroll">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        <div className="verify-number-main">
                            <TopTabbar />
                            <Header />
                            <div className="verify-number-top">
                                <div className="container">
                                    <div className="home-top-content">
                                        <div className="person-details" style={{ marginTop: '49px' }}>
                                            <p className="name" style={{ fontSize: '25px' }}>Modification du mot de Passe</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="verify-number-bottom" id="faq-main">
                                <div className="verify-number-bottom-wrap">
                                    <div className="verify-number-content">
                                        <div className="faq-full-sec">
                                            <div className="container py-4">
                                                <form onSubmit={handleSubmit}>
                                                    <div className="mb-3">
                                                        <label htmlFor="oldPassword" className="form-label">Ancien mot de passe</label>
                                                        <div className="input-group">
                                                            <input
                                                                type={showOldPassword ? 'text' : 'password'}
                                                                className="form-control"
                                                                id="oldPassword"
                                                                value={oldPassword}
                                                                onChange={(e) => setOldPassword(e.target.value)}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={toggleOldPasswordVisibility}
                                                            >
                                                                <i className={`fa-solid ${showOldPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label htmlFor="newPassword" className="form-label">Nouveau mot de passe</label>
                                                        <div className="input-group">
                                                            <input
                                                                type={showNewPassword ? 'text' : 'password'}
                                                                className={`form-control ${newPasswordError ? 'is-invalid' : ''}`}
                                                                id="newPassword"
                                                                value={newPassword}
                                                                onChange={handleNewPasswordChange}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={toggleNewPasswordVisibility}
                                                            >
                                                                <i className={`fa-solid ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                            </button>
                                                            {newPasswordError && <div className="invalid-feedback">{newPasswordError}</div>}
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label htmlFor="confirmNewPassword" className="form-label">Confirmer le nouveau mot de passe</label>
                                                        <div className="input-group">
                                                            <input
                                                                type={showConfirmNewPassword ? 'text' : 'password'}
                                                                className={`form-control ${confirmNewPasswordError ? 'is-invalid' : ''}`}
                                                                id="confirmNewPassword"
                                                                value={confirmNewPassword}
                                                                onChange={handleConfirmNewPasswordChange}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={toggleConfirmNewPasswordVisibility}
                                                            >
                                                                <i className={`fa-solid ${showConfirmNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                            </button>
                                                            {confirmNewPasswordError && <div className="invalid-feedback">{confirmNewPasswordError}</div>}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <span>
                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                {' '}Chargement...
                                                            </span>
                                                        ) : (
                                                            'Modifier le mot de passe'
                                                        )}
                                                    </button>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPasswordPage;